// src/api/generalChatApi.js
//
// Consume the /api/chat SSE-over-POST stream with fetch() + getReader() +
// TextDecoder. EventSource is GET-only and cannot send the POST body, so we
// parse the SSE frames by hand.
//
// Server event protocol:
//   event: meta   data: {<audit>}        -> onMeta(audit)   (once, before tokens)
//   event: token  data: {"text": "..."}  -> onToken(text)   (repeated)
//   event: done   data: {}               -> onDone()
//   event: error  data: {"message": ...} -> onError(message)

import { API_BASE } from "../utils/api";

export async function streamGeneralChat(
  { conversationId, message, allowWebSearch, deepThinking, attachmentIds },
  handlers = {}
) {
  return streamChatAt(
    "/chat",
    { conversationId, message, allowWebSearch, deepThinking, attachmentIds },
    handlers
  );
}

// The SSE-over-POST reader, shared by every chat surface that speaks this
// protocol. `path` is appended to API_BASE (e.g. "/chat", "/research-chat/chat").
export async function streamChatAt(
  path,
  payload,
  { onMeta, onSources, onToken, onDone, onError, signal } = {}
) {
  const token = localStorage.getItem("token");

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (e) {
    if (e.name === "AbortError") return;
    onError && onError("Network error");
    return;
  }

  // Pre-stream failure (validation, or an upstream that never opened) arrives
  // as a JSON error body rather than an SSE frame.
  if (!res.ok || !res.body) {
    let msg = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      if (j && j.error) msg = j.error;
    } catch {
      /* non-JSON error body */
    }
    onError && onError(msg);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line.
      let sep;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        handleFrame(frame, { onMeta, onSources, onToken, onDone, onError });
      }
    }
  } catch (e) {
    if (e.name === "AbortError") return;
    onError && onError("Stream interrupted");
  }
}

function handleFrame(frame, { onMeta, onSources, onToken, onDone, onError }) {
  let event = "message";
  const dataLines = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    // Ignore comment/keep-alive lines (":...").
  }
  if (!dataLines.length) return;

  let data;
  try {
    data = JSON.parse(dataLines.join("\n"));
  } catch {
    return;
  }

  switch (event) {
    case "meta":
      onMeta && onMeta(data);
      break;
    case "sources":
      onSources && onSources(data.sources || []);
      break;
    case "token":
      onToken && onToken(data.text || "");
      break;
    case "done":
      onDone && onDone(data);
      break;
    case "error":
      onError && onError(data.message || "Generation failed");
      break;
    default:
      break;
  }
}

/* -----------------------------------------------------------------------------
   Conversation history (list / load / rename / pin / delete)
   ---------------------------------------------------------------------------*/

export function authHeaders(json = false) {
  const token = localStorage.getItem("token");
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/* -----------------------------------------------------------------------------
   Attachments (upload / delete) — ephemeral, per-turn, documents only (v1)
   ---------------------------------------------------------------------------*/

// Upload file(s) for the next turn. Returns { attachments: [{id, filename, kind,
// sizeBytes, charCount, truncated} | {filename, error}] }. Do NOT set
// Content-Type — the browser must set the multipart boundary itself.
export async function uploadAttachments(conversationId, files) {
  const fd = new FormData();
  if (conversationId) fd.append("conversationId", conversationId);
  for (const f of files) fd.append("file", f);

  const res = await fetch(`${API_BASE}/chat/attachments`, {
    method: "POST",
    headers: authHeaders(false),
    body: fd,
  });
  let json = null;
  try {
    json = await res.json();
  } catch {
    /* non-JSON */
  }
  if (!res.ok) {
    const msg = (json && json.error) || `upload failed (${res.status})`;
    const err = new Error(msg);
    err.attachments = (json && json.attachments) || [];
    throw err;
  }
  return json || { attachments: [] };
}

export async function deleteAttachment(id) {
  const res = await fetch(
    `${API_BASE}/chat/attachments/${encodeURIComponent(id)}`,
    { method: "DELETE", headers: authHeaders() }
  );
  if (!res.ok && res.status !== 204) throw new Error(`delete failed (${res.status})`);
  return true;
}

export async function listConversations() {
  const res = await fetch(`${API_BASE}/general-chat/conversations`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`list failed (${res.status})`);
  return res.json();
}

export async function loadConversation(id) {
  const res = await fetch(
    `${API_BASE}/general-chat/conversations/${encodeURIComponent(id)}`,
    { headers: authHeaders() }
  );
  if (!res.ok) throw new Error(`load failed (${res.status})`);
  return res.json();
}

export async function renameConversation(id, title) {
  const res = await fetch(
    `${API_BASE}/general-chat/conversations/${encodeURIComponent(id)}`,
    { method: "PATCH", headers: authHeaders(true), body: JSON.stringify({ title }) }
  );
  if (!res.ok) throw new Error(`rename failed (${res.status})`);
  return res.json();
}

export async function setConversationPinned(id, pinned) {
  const res = await fetch(
    `${API_BASE}/general-chat/conversations/${encodeURIComponent(id)}`,
    { method: "PATCH", headers: authHeaders(true), body: JSON.stringify({ pinned }) }
  );
  if (!res.ok) throw new Error(`pin failed (${res.status})`);
  return res.json();
}

export async function deleteConversation(id) {
  const res = await fetch(
    `${API_BASE}/general-chat/conversations/${encodeURIComponent(id)}`,
    { method: "DELETE", headers: authHeaders() }
  );
  if (!res.ok && res.status !== 204) throw new Error(`delete failed (${res.status})`);
  return true;
}
